import React from "react";
import { 
  FaFacebookF, 
  FaInstagram, 
  FaXTwitter, 
  FaYoutube 
} from "react-icons/fa6";
import { 
  IoMdMail, 
  IoMdCall 
} from "react-icons/io";
import { 
  MdLocationOn, 
  MdArrowForward 
} from "react-icons/md";
import { useTenant } from "../Context/TenantContext";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "./UserContext";

const Footer = () => {
  const navigate = useNavigate();
  const { user } = React.useContext(UserContext);
  const {
    getLogoUrl,
    getCollegeName,
    getCollegePhone,
    getCollegeEmail,
    getCollegeLocation,
    isWhiteLabelEnabled,
    tenant
  } = useTenant();

  const isMainPlatform = !tenant ||
                        tenant.domain === 'theskillgenie.com' ||
                        tenant.tenantKey === 'theskillgenie' ||
                        window.location.hostname === 'theskillgenie.com' ||
                        window.location.hostname === 'www.theskillgenie.com' ||
                        window.location.hostname === 'localhost';

  const phone = getCollegePhone() || "+91 62815 97586";
  const email = getCollegeEmail() || "connect@evolvexinnovations.org";
  const location = getCollegeLocation() || "Bhoomatha Thalluri Heights, Kankipadu, Vijayawada – 521151";

  const protectedLinks = [
    "Technologies",
    "Bootcamps",
    "Mock Interviews",
    "AI Tutor",
    "Compiler",
    "Documentation"
  ];

  const handleLinkClick = (e, link) => {
    if (protectedLinks.includes(link.name) && !user) {
      e.preventDefault();
      navigate("/login");
    }
  };

  const footerLinks = [
    {
      title: "Platform",
      links: [
        { name: "Home", path: "/" },
        { name: "Technologies", path: "/techgrid" },
        { name: "Bootcamps", path: "/bootcamp/enrollment" },
        { name: "Pricing", path: "/subscription-plans" },
      ]
    },
    {
      title: "Resources",
      links: [
        { name: "Documentation", path: "/techmcq" },
        { name: "AI Tutor", path: "/tsg" },
        { name: "Compiler", path: "/compiler" },
        { name: "Mock Interviews", path: "/mockinterviewhome" },
      ]
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", path: "/privacy-policy" },
        { name: "Terms of Service", path: "/terms-of-service" },
        { name: "Contact Us", path: "/contact" },
      ]
    }
  ];

  return (
    <footer className="bg-[#0F172A] text-white pt-24 pb-12 px-6 md:px-12 overflow-hidden relative font-sans">
      {/* Background decoration */}
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[100px] -z-10"></div>
      
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-20">
          {/* Brand Column */}
          <div className="lg:col-span-5 space-y-8">
            <div className="flex items-center gap-3">
              <img
                src={getLogoUrl()}
                alt={`${isMainPlatform ? "The Skill Genie" : getCollegeName()} Logo`}
                className="w-12 h-12 rounded-xl bg-white p-1 object-contain border border-white/20"
                onError={(e) => { e.target.src = "/logo192.png"; }}
              />
              <span className="text-2xl font-bold tracking-tighter text-white">
                {isMainPlatform ? "The Skill Genie" : getCollegeName()}
              </span>
            </div>
            
            <p className="text-purple-100 text-lg leading-relaxed max-w-md">
              {isMainPlatform ?
                "The world's most advanced AI-powered learning and career development platform. Empowering the next generation of engineers with industry-ready skills." :
                `Empowering ${getCollegeName()} students with industry-ready skills through our AI-driven learning platform, ensuring global career success.`
              }
            </p>
            
            <div className="flex gap-4">
              {[
                { icon: <FaFacebookF />, url: "https://facebook.com/theskillgenie" },
                { icon: <FaXTwitter />, url: "https://x.com/EvolveX2025" },
                { icon: <FaInstagram />, url: "https://www.instagram.com/theskillgenie_" },
                { icon: <FaYoutube />, url: "https://www.youtube.com/@EvolveXInnovations" }
              ].map((social, i) => (
                <a 
                  key={i} 
                  href={social.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-[#843FEF] hover:border-white transition-all duration-300"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
          
          {/* Links Columns */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-12">
            {footerLinks.map((section, idx) => (
              <div key={idx} className="space-y-6">
                <h4 className="text-sm font-bold uppercase tracking-[2px] text-white/60">{section.title}</h4>
                <ul className="space-y-4">
                  {section.links.map((link, i) => (
                    <li key={i}>
                      <Link 
                        to={link.path} 
                        onClick={(e) => handleLinkClick(e, link)}
                        className="text-purple-100 hover:text-white transition-colors flex items-center group font-medium"
                      >
                        <span className="w-0 overflow-hidden group-hover:w-4 transition-all duration-300 text-white">
                           <MdArrowForward className="h-3 w-3" />
                        </span>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        
        {/* Contact Strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-10 border-y border-white/10 mb-10">
           <div className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#843FEF] transition-all border border-white/20 shadow-sm">
                 <IoMdCall size={24} />
              </div>
              <div>
                 <p className="text-[10px] font-bold text-white/60 uppercase">Call Support</p>
                 <a href={`tel:${phone}`} className="text-lg font-bold text-white hover:text-white/80 transition-colors">{phone}</a>
              </div>
           </div>
           
           <div className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#843FEF] transition-all border border-white/20 shadow-sm">
                 <IoMdMail size={24} />
              </div>
              <div>
                 <p className="text-[10px] font-bold text-white/60 uppercase">Email Us</p>
                 <a href={`mailto:${email}`} className="text-lg font-bold text-white hover:text-white/80 transition-colors">{email}</a>
              </div>
           </div>
           
           <div className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#843FEF] transition-all border border-white/20 shadow-sm">
                 <MdLocationOn size={24} />
              </div>
              <div>
                 <p className="text-[10px] font-bold text-white/60 uppercase">Visit Us</p>
                 <span className="text-sm font-bold text-white leading-tight block">{location}</span>
              </div>
           </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-purple-100 text-sm">
            © {new Date().getFullYear()} <span className="text-white font-bold tracking-tighter">{isMainPlatform ? "The Skill Genie" : getCollegeName()}</span>. All rights reserved.
          </p>
          
          <div className="flex items-center gap-2 text-purple-100 text-sm">
             Made with <span className="text-white animate-pulse">❤</span> by <a href="https://evolvexinnovations.org" target="_blank" className="text-white hover:text-white/80 transition-colors font-bold">EvolveX Innovations</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;



